import z from 'zod'
import { Category, Unit } from './products'
import { externalOrderSchema, getOrderSchema, orderItemSchema } from './schemas'
import { OrderPlace } from './work'

export type DeliveryCompany = {
  id: string
  name: string
  isActive: boolean
  shipmentTypes: Array<ShipmentType>
}

export enum DeliveryCompanyName {
  InPost = 'InPost',
  DPD = 'DPD',
  Poczta = 'Poczta Polska',
}

export type ShipmentType = {
  id: string
  name: string
  isActive: boolean
  isParcelLocker: boolean
  shippingCost: number
}

export type NewShipmentType = Omit<ShipmentType, 'id'>
export type NewDeliveryCompany = Omit<DeliveryCompany, 'id' | 'shipmentTypes'> & {
  shipmentTypes: Array<NewShipmentType>
}

export enum HistoryCategory {
  LACKS = 'LACKS',
  CUTTING = 'CUTTING',
  SEWING = 'SEWING',
  EYELET = 'EYELET',
  MANUAL_ORDER = 'MANUAL_ORDER',
}

export type OrderHistory = {
  date: Date
  user: {
    id: number
    fullName: string
  }
  orderPlace: OrderPlace | null
  description: string
  comments: string | null
  category: HistoryCategory
}

type OrderPreviewForHistory = {
  id: string
  createdAt: Date | null
  firstName: string | null
  lastName: string | null
  email: string | null
  countryCode: number | null
  phoneNumber: string | null
}

export type OrderWithHistory = {
  order: OrderPreviewForHistory
  history: Array<OrderHistory>
}

export enum DocumentType {
  INVOICE = 'INVOICE',
  RECEIPT = 'RECEIPT',
}

export enum PaymentMethod {
  ON_DELIVERY = 'ON_DELIVERY',
  PREPAID = 'PREPAID',
}

export type NewOrderItem = z.infer<typeof orderItemSchema> & { id: string }

export type NewOrder = z.infer<ReturnType<typeof getOrderSchema>> & {
  items: Array<NewOrderItem>
  totalPrice: number
}

export type UpdateOrder = NewOrder & { id: number; dispatched?: boolean }

type OrderItem<DateType = string> = {
  id: string
  createdAt: DateType
  amount: number
  unit: Unit
  price: number
  productSymbol: string
  size: string
  category: Category
  finish: string
  comments: string
}

export type Order = {
  id: number
  createdAt: string
  dispatchedAt: string | null
  firstName: string
  lastName: string
  countryCode: number
  phoneNumber: string
  email: string
  trackingNumber: string
  deliveryAddress: {
    name?: string
    city?: string
    street?: string
    buildingNumber?: string
    apartmentNumber?: string
    postalCode?: string
    country?: string
    firstName: string
    lastName: string
    parcelLocker?: string
    parcelSize?: string
  }
  shipmentType: {
    id: string
    name: string
    isActive: boolean
    isParcelLocker: boolean
    shippingCost: number
    deliveryCompanyId: string
    deliveryCompanyName: DeliveryCompanyName
  }
  shippingCost: number
  comments: string
  documentType: DocumentType
  nip: string
  paymentMethod: PaymentMethod
  discountPercent: number
  orderItems: Array<OrderItem>
  totalPrice: number
  orderConfirmationSentAt: string | null
  dispatchConfirmationSentAt: string | null
}

export type OrderTableItem = Omit<Order, 'createdAt' | 'dispatchedAt' | 'orderItems'> & {
  createdAt: Date
  dispatchedAt: Date | null
  orderItems: Array<OrderItem<Date>>
}

export type ShipmentResponse = {
  orderId: number
  shipmentId: number
  trackingNumber: string | null
  errorMessage: string | null
}

export type NewExternalOrder = z.infer<typeof externalOrderSchema>

export type ExternalOrder = NewExternalOrder & {
  id: number
}
